/**
 * ErrorSegnaloBanner — F-005 Phase 4 (IMPL-P4-004).
 *
 * Banner di emergenza mostrato in cima alla `PraticaDetailPage` quando lo
 * stato è BLOCCATA. Elenca le ragioni di blocco scelte dall'operatore
 * (mappate via `SEGNALAZIONE_REASON_LABELS`), eventuale dettaglio testuale
 * e un CTA che porta al wizard di correzione.
 *
 * Accessibilità:
 *   - shadcn `<Alert>` ha già `role="alert"` ma è ridichiarato esplicitamente
 *     nel test contract per chiarezza
 *   - `aria-live="assertive"` interrompe gli SR (correzione richiesta = urgente)
 */
import { AlertTriangle } from 'lucide-react';
import { SEGNALAZIONE_REASON_LABELS } from '@app/shared';
import type { SegnalazioneReason } from '@app/shared';
import { Alert, AlertDescription, AlertTitle } from '@/components/ui/alert';
import { CorrectionCTA } from './CorrectionCTA';

interface ErrorSegnaloBannerProps {
  praticaId: string;
  blockedReasons: SegnalazioneReason[];
  blockedDetail: string | null;
}

export function ErrorSegnaloBanner({
  praticaId,
  blockedReasons,
  blockedDetail,
}: ErrorSegnaloBannerProps) {
  return (
    <Alert variant="destructive" role="alert" aria-live="assertive">
      <AlertTriangle className="h-4 w-4" aria-hidden="true" />
      <AlertTitle>Pratica bloccata — correzione richiesta</AlertTitle>
      <AlertDescription>
        {blockedReasons.length > 0 && (
          <ul className="mt-1 list-disc pl-4 text-sm">
            {blockedReasons.map((r) => (
              <li key={r}>{SEGNALAZIONE_REASON_LABELS[r] ?? r}</li>
            ))}
          </ul>
        )}
        {blockedDetail && <p className="mt-2 text-sm">{blockedDetail}</p>}
        <CorrectionCTA praticaId={praticaId} className="mt-3" />
      </AlertDescription>
    </Alert>
  );
}
